import React from "react";
import Image from "src/shared/Image";
import { useImageFallback } from "src/business-types/laundry/modules/home/hooks/useImageFallback";
import { Product } from "src/shared/services/interfacev2";

interface ProductGridProps {
  products: Product[];
  onProductClick: (product: Product) => void;
}

interface ProductCardProps {
  item: Product;
  onProductClick: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ item, onProductClick }) => {
  const { imgSrc } = useImageFallback(item?.imageUrl || "");

  return (
    <div
      onClick={() => onProductClick(item)}
      className="
        group bg-white border border-gray-100 
        rounded-xl p-4 flex flex-col items-center 
        cursor-pointer transition-all duration-200
        hover:shadow-md hover:border-blue-200 hover:scale-[1.02]
        h-56
      "
    >
      {/* Product Image Container */}
      <div className="w-full flex items-center justify-center mb-3 h-32">
        <div className="relative w-28 h-28 flex items-center justify-center">
          <Image imagePath={imgSrc} className="w-full h-full object-contain" />
        </div>
      </div>

      {/* Product Name */}
      <div className="flex-grow w-full mb-3">
        <p
          className="
          text-sm font-medium text-gray-700 
          text-center line-clamp-2 
          group-hover:text-blue-600
          leading-tight
        "
        >
          {item.productName}
        </p>
      </div>

      {/* Price */}
      <div className="w-full flex justify-center">
        <p
          className="
          text-base font-semibold text-green-600 
          bg-green-50 px-3 py-1.5 rounded-full
          min-w-[80px] text-center
        "
        >
          {item.amount}
        </p>
      </div>
    </div>
  );
};

const ProductGrid: React.FC<ProductGridProps> = ({ products, onProductClick }) => {
  if (!products?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 text-center">
        <div className="text-gray-300 mb-3">
          <svg className="w-14 h-14 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-500">No products available</h3>
        <p className="text-sm text-gray-400 mt-1">Try another category or check back later.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
      {products.map((item) => (
        <ProductCard key={typeof item._id === "object" ? item._id.$oid : item._id} item={item} onProductClick={onProductClick} />
      ))}
    </div>
  );
};

export default ProductGrid;
